import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  getOrganisationXeroAccounts,
  getOrganisationXeroTaxRate,
  getXeroTrackingCategories
} from "src/network/graphql/xeroServices";
import {
  getOrganisationQuickBooksAccounts,
  getOrganisationQuickBooksCategories,
  getOrganisationQuickBooksTaxCodes
} from "src/network/graphql/quickbooksServices";
import type { XeroAccount, XeroTaxType, XeroTrackingCategories } from "./xeroSlice";

const XERO_META_TTL_SECONDS = 300;

export type XeroMetaData = {
  xeroAccounts: XeroAccount[];
  xeroTaxTypes: XeroTaxType[];
  xeroTrackingCategories: XeroTrackingCategories[];
};

// The reference-data cache is keyed by `<provider>:<tenantId>` so switching an
// org between providers never serves the other provider's codes. Legacy keys
// with no prefix are treated as Xero.
export type MetaProvider = "xero" | "quickbooks";

export const buildMetaKey = (provider: MetaProvider, tenantId: string) => `${provider}:${tenantId}`;

const parseMetaKey = (key: string): { provider: MetaProvider; tenantId: string } => {
  const idx = key.indexOf(":");

  if (idx === -1) return { provider: "xero", tenantId: key };
  const provider = key.slice(0, idx);

  return {
    provider: provider === "quickbooks" ? "quickbooks" : "xero",
    tenantId: key.slice(idx + 1)
  };
};

// QuickBooks reference data comes back in the same field-name dialect as Xero
// (accountId / taxType / trackingCategoryID — adapted server-side), so the
// same XeroMetaData shape serves every consumer unchanged.
const fetchProviderMeta = async (provider: MetaProvider) => {
  if (provider === "quickbooks") {
    const [qbAccountsRes, qbTaxCodesRes, qbCategoriesRes] = await Promise.all([
      getOrganisationQuickBooksAccounts(),
      getOrganisationQuickBooksTaxCodes(),
      getOrganisationQuickBooksCategories()
    ]);

    return {
      accounts: (qbAccountsRes.data?.get_organisation_quickbooks_accounts ?? []) as XeroAccount[],
      taxRates: (qbTaxCodesRes?.data?.get_organisation_quickbooks_tax_codes ?? []) as XeroTaxType[],
      categories: (qbCategoriesRes.data?.get_organisation_quickbooks_categories ?? []) as XeroTrackingCategories[]
    };
  }
  const accountsRes = await getOrganisationXeroAccounts();
  const taxRatesRes = await getOrganisationXeroTaxRate();
  const categoriesRes = await getXeroTrackingCategories();

  return {
    accounts: (accountsRes.data?.get_organisation_xero_accounts ?? []) as XeroAccount[],
    taxRates: (taxRatesRes?.data?.get_organisation_xero_tax_rate ?? []) as XeroTaxType[],
    categories: (categoriesRes.data?.get_organisation_xero_categories ?? []) as XeroTrackingCategories[]
  };
};

export const xeroMetaApi = createApi({
  reducerPath: "xeroMetaApi",
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getXeroMeta: builder.query<XeroMetaData, string>({
      async queryFn(metaKey) {
        try {
          const { provider } = parseMetaKey(metaKey);
          const { accounts, taxRates, categories } = await fetchProviderMeta(provider);

          const xeroAccounts: XeroAccount[] = accounts;

          const rawTaxRates: XeroTaxType[] = taxRates;
          const xeroTaxTypes = Array.from(
            rawTaxRates
              .reduce((acc, item) => {
                const taxType = item?.taxType;

                if (!taxType || acc.has(taxType)) return acc;
                acc.set(taxType, {
                  taxType,
                  name: item?.name || taxType,
                  hidden: item?.hidden
                });
                return acc;
              }, new Map<string, XeroTaxType>())
              .values()
          );

          const xeroTrackingCategories: XeroTrackingCategories[] = categories;

          return {
            data: {
              xeroAccounts,
              xeroTaxTypes,
              xeroTrackingCategories
            }
          };
        } catch (error) {
          return {
            error: error as unknown
          };
        }
      },
      keepUnusedDataFor: XERO_META_TTL_SECONDS
    })
  })
});

export const { useGetXeroMetaQuery } = xeroMetaApi;
export { XERO_META_TTL_SECONDS };
